import { Injectable } from '@angular/core';
import {Sort} from "../models/sort";
import {HttpClient, HttpParams, HttpEventType} from "@angular/common/http";
import {SortHelper} from "../utils/sort-helper";
import {PageResponse} from "../models/page-response";
import {environment} from "../../environments/environment";
import {Project} from "../models/project";
import {PointCloud} from "../models/point-cloud";
import {PointCloudModificationInput} from "../models/point-cloud-modification-input";
import {Observable} from "rxjs";
import {map, filter} from "rxjs/operators";

@Injectable({
  providedIn: 'root'
})
export class PointCloudService {

  constructor(private httpClient: HttpClient) { }

  list(project: Project, page: number, sort?: Sort, filters: HttpParams = new HttpParams()) {
    let params = filters.set('page', page.toString());

    if (sort) {
      params = params.set('sort', SortHelper.convertSortToString(sort));
    }

    return this.httpClient.get<PageResponse<PointCloud>>(`${environment.api}/api/v1/point-clouds/projects/${project.id}`, {
      params,
    });
  }

  all(project: Project) {
    return this.httpClient.get<PointCloud[]>(`${environment.api}/api/v1/point-clouds/projects/${project.id}/all`);
  }

  detail(id: number|string) {
    return this.httpClient.get<PointCloud>(`${environment.api}/api/v1/point-clouds/${id}`);
  }

  startChunkedUpload(project: Project, input: any): Observable<PointCloud> {
    return this.httpClient.post<PointCloud>(`${environment.api}/api/v1/point-clouds/projects/${project.id}/start`, input);
  }

  uploadChunk(pointCloudId: number, chunk: Blob, chunkIndex: number, totalChunks: number, progressCallback?: (progress: number) => void): Observable<PointCloud> {
    const formData = new FormData();
    formData.append('chunk', chunk, `chunk_${chunkIndex}`);
    
    return this.httpClient.post<PointCloud>(`${environment.api}/api/v1/point-clouds/${pointCloudId}/chunks`, formData, {
      params: {
        chunkIndex: chunkIndex.toString(),
        totalChunks: totalChunks.toString()
      },
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress && progressCallback) {
          const progress = event.loaded / (event.total || 1);
          progressCallback(progress);
        }
        
        if (event.type === HttpEventType.Response) {
          return event.body as PointCloud;
        }
        
        return null;
      }),
      filter(result => result !== null)
    ) as Observable<PointCloud>;
  }

  finalizeChunkedUpload(pointCloudId: number, originalFilename: string, totalChunks: number): Observable<PointCloud> {
    return this.httpClient.post<PointCloud>(`${environment.api}/api/v1/point-clouds/${pointCloudId}/finalize`, {}, {
      params: {
        originalFilename: originalFilename,
        totalChunks: totalChunks.toString()
      }
    });
  }

  download(entity: PointCloud) {
    return this.httpClient.get(`${environment.api}/api/v1/point-clouds/${entity.id}/download`, {
      responseType: 'blob',
    });
  }

  convertLas(entity: PointCloud) {
    return this.httpClient.get(`${environment.api}/api/v1/point-clouds/${entity.id}/convert/las`, {
      responseType: 'blob',
    });
  }

  tagsShapefile(entity: PointCloud) {
    return this.httpClient.get(`${environment.api}/api/v1/point-clouds/${entity.id}/shapefile/tags`, {
      responseType: 'blob',
    });
  }

  downloadMultiple(project: Project, ids: number[]) {
    return this.httpClient.get(`${environment.api}/api/v1/point-clouds/download/multiple`, {
      params: new HttpParams().set('ids', ids.join(',')),
      responseType: 'blob',
    });
  }

  convertLasMultiple(project: Project, ids: number[]) {
    return this.httpClient.get(`${environment.api}/api/v1/point-clouds/convert/las/multiple`, {
      params: new HttpParams().set('ids', ids.join(',')),
      responseType: 'blob',
    });
  }

  tagsShapefileMultiple(project: Project, ids: number[]) {
    return this.httpClient.get(`${environment.api}/api/v1/point-clouds/shapefile/tags/multiple`, {
      params: new HttpParams().set('ids', ids.join(',')),
      responseType: 'blob',
    });
  }

  delete(entity: PointCloud) {
    return this.httpClient.delete(`${environment.api}/api/v1/point-clouds/${entity.id}`);
  }

  modifyCoordinates(pointCloud: PointCloud, input: PointCloudModificationInput) {
    return this.httpClient.post<PointCloud>(`${environment.api}/api/v1/point-clouds/${pointCloud.id}/modify/coordinates`, input);
  }

  listUnmatched(page: number, sort?: Sort, filters: HttpParams = new HttpParams()) {
    let params = filters.set('page', page.toString());

    if (sort) {
      params = params.set('sort', SortHelper.convertSortToString(sort));
    }

    return this.httpClient.get<PageResponse<PointCloud>>(`${environment.api}/api/v1/point-clouds/unmatched`, {
      params,
    });
  }
}
